'use client'

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MdCheck, MdClose, MdExpandMore, MdSearch } from 'react-icons/md'

export interface SearchableOption {
    value: number | string
    /** Texto principal exibido e usado na busca. */
    label: string
    /** Linha secundária (ex: preço formatado). Também entra na busca. */
    hint?: string
    disabled?: boolean
}

interface Props {
    value: number | string | null
    onChange: (value: number | string | null) => void
    options: SearchableOption[]
    label?: string
    placeholder?: string
    /** Rótulo da opção que limpa a seleção. Se ausente, não há opção vazia. */
    emptyLabel?: string
    disabled?: boolean
    className?: string
    /** Acima deste total de opções, o campo de busca aparece. */
    searchThreshold?: number
}

/**
 * Combobox com busca. Substitui o `<select>` nativo quando a lista é grande —
 * o caso que motivou este componente foi "IgE, por alérgeno (cada)", que tem
 * 169 variações (uma por alérgeno) e ficava impraticável num select nativo.
 *
 * A busca é acento-insensível: "acaro" acha "Ácaros D. Pterony (D1)".
 *
 * O menu é renderizado num PORTAL (`position: fixed`) para não ser cortado por
 * ancestrais com `overflow` (tabelas com scroll horizontal, cards, etc.) — o
 * `z-index` sozinho não resolve clipping de overflow. A posição é recalculada
 * em scroll/resize para seguir o botão.
 */
export function SearchableSelect({
    value,
    onChange,
    options,
    label,
    placeholder = 'Buscar...',
    emptyLabel,
    disabled = false,
    className = '',
    searchThreshold = 8,
}: Props) {
    const [open, setOpen] = useState(false)
    const [term, setTerm] = useState('')
    const containerRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const [menuPos, setMenuPos] = useState<{
        top: number
        left: number
        width: number
    } | null>(null)

    const selected = useMemo(
        () => options.find(o => o.value === value) ?? null,
        [options, value]
    )
    const showSearch = options.length >= searchThreshold

    const norm = (s: string) =>
        s
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .toLowerCase()

    const filtered = useMemo(() => {
        const t = norm(term.trim())
        if (!t) return options
        return options.filter(
            o => norm(o.label).includes(t) || norm(o.hint ?? '').includes(t)
        )
    }, [options, term])

    // Posiciona o menu (fixed) logo abaixo do botão, com a largura dele.
    const updatePosition = () => {
        const el = triggerRef.current
        if (!el) return
        const r = el.getBoundingClientRect()
        setMenuPos({ top: r.bottom + 4, left: r.left, width: r.width })
    }

    useLayoutEffect(() => {
        if (!open) return
        updatePosition()
        // `true` (capture) pega scroll de QUALQUER container ancestral, não só o window.
        const onScroll = () => updatePosition()
        window.addEventListener('scroll', onScroll, true)
        window.addEventListener('resize', onScroll)
        return () => {
            window.removeEventListener('scroll', onScroll, true)
            window.removeEventListener('resize', onScroll)
        }
    }, [open])

    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            const target = e.target as Node
            if (
                containerRef.current?.contains(target) ||
                menuRef.current?.contains(target)
            ) {
                return
            }
            setOpen(false)
            setTerm('')
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    useEffect(() => {
        if (open && showSearch) inputRef.current?.focus()
    }, [open, showSearch])

    const pick = (v: number | string | null) => {
        onChange(v)
        setOpen(false)
        setTerm('')
    }

    return (
        <div className={`form-control w-full ${className}`} ref={containerRef}>
            {label && (
                <label className="label">
                    <span className="label-text">{label}</span>
                </label>
            )}

            <div className="relative">
                <button
                    ref={triggerRef}
                    type="button"
                    className="input input-bordered w-full flex items-center justify-between gap-2 text-left font-normal"
                    onClick={() => !disabled && setOpen(o => !o)}
                    disabled={disabled}
                >
                    <span
                        className={`truncate ${selected ? '' : 'text-base-content/50'}`}
                    >
                        {selected
                            ? selected.hint
                                ? `${selected.label} — ${selected.hint}`
                                : selected.label
                            : (emptyLabel ?? placeholder)}
                    </span>
                    <MdExpandMore className="shrink-0 text-base-content/50" />
                </button>

                {open &&
                    menuPos &&
                    createPortal(
                        <div
                            ref={menuRef}
                            className="fixed z-[100] bg-base-100 border border-base-300 rounded-lg shadow-xl"
                            style={{
                                top: menuPos.top,
                                left: menuPos.left,
                                width: menuPos.width,
                            }}
                        >
                            {showSearch && (
                                <div className="p-2 border-b border-base-300">
                                    <div className="relative">
                                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            className="input input-bordered input-sm w-full pl-9"
                                            placeholder={placeholder}
                                            value={term}
                                            onChange={e => setTerm(e.target.value)}
                                        />
                                        {term && (
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs btn-circle absolute right-1 top-1/2 -translate-y-1/2"
                                                onClick={() => setTerm('')}
                                                aria-label="Limpar busca"
                                            >
                                                <MdClose />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            <ul className="max-h-64 overflow-y-auto py-1">
                                {emptyLabel && !term && (
                                    <li>
                                        <button
                                            type="button"
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-base-200 flex items-center gap-2"
                                            onClick={() => pick(null)}
                                        >
                                            {value == null && (
                                                <MdCheck className="text-secondary shrink-0" />
                                            )}
                                            <span
                                                className={
                                                    value == null ? '' : 'ml-6'
                                                }
                                            >
                                                {emptyLabel}
                                            </span>
                                        </button>
                                    </li>
                                )}

                                {filtered.length === 0 && (
                                    <li className="px-3 py-3 text-center text-sm text-base-content/60">
                                        Nenhuma opção encontrada
                                    </li>
                                )}

                                {filtered.map(o => {
                                    const isSel = o.value === value
                                    return (
                                        <li key={o.value}>
                                            <button
                                                type="button"
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-base-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-start gap-2"
                                                onClick={() => pick(o.value)}
                                                disabled={o.disabled}
                                            >
                                                {isSel ? (
                                                    <MdCheck className="text-secondary shrink-0 mt-0.5" />
                                                ) : (
                                                    <span className="w-4 shrink-0" />
                                                )}
                                                <span className="min-w-0 flex-1">
                                                    <span className="block truncate">
                                                        {o.label}
                                                    </span>
                                                    {o.hint && (
                                                        <span className="block text-xs text-base-content/60">
                                                            {o.hint}
                                                        </span>
                                                    )}
                                                </span>
                                            </button>
                                        </li>
                                    )
                                })}
                            </ul>

                            {showSearch && filtered.length > 0 && (
                                <div className="px-3 py-1.5 border-t border-base-300 text-xs text-base-content/50">
                                    {filtered.length} de {options.length}
                                </div>
                            )}
                        </div>,
                        document.body
                    )}
            </div>
        </div>
    )
}
